/**
 * ActiveCollections Component
 * 
 * Displays currently active menu collections (Valentine's, Birthday, etc.)
 * on the homepage or menu page. Only shows collections that are:
 * - is_active = true
 * - show_on_homepage = true (when used on homepage)
 * - within start_date/end_date range
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from '@heroicons/react/24/outline';

// API URL
const API_URL = import.meta.env.VITE_API_URL || '';

interface Collection {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  image_url: string | null;
  is_active: boolean;
  show_on_homepage: boolean;
}

interface ActiveCollectionsProps {
  /** Only show collections marked for homepage */
  homepageOnly?: boolean;
  /** Custom title for the section */
  title?: string;
  /** Custom subtitle */
  subtitle?: string;
  /** Maximum number of collections to show */
  limit?: number;
}

export default function ActiveCollections({ 
  homepageOnly = true, 
  title = "Seasonal Specials",
  subtitle = "Limited time menus and special offerings",
  limit = 4
}: ActiveCollectionsProps) {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const response = await fetch(`${API_URL}/api/collections?active=true`);
        if (!response.ok) return;
        
        const data = await response.json();
        let items = data.collections || [];
        
        // Filter for homepage if needed
        if (homepageOnly) {
          items = items.filter((c: Collection) => c.show_on_homepage);
        }
        
        // Apply limit
        if (limit) {
          items = items.slice(0, limit);
        }
        
        setCollections(items);
      } catch (err) {
        console.error('Failed to fetch collections:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCollections();
  }, [homepageOnly, limit]);

  // Don't render anything if no active collections
  if (loading || collections.length === 0) {
    return null;
  }

  return (
    <section className="py-16 bg-gradient-to-r from-pink-50 to-purple-50">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <span className="inline-block px-4 py-1 bg-pink-100 text-pink-700 rounded-full text-sm font-medium mb-4">
            ✨ Limited Time
          </span>
          <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-3">
            {title}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            {subtitle}
          </p>
        </motion.div>

        {/* Collections Grid */}
        <div className={`grid gap-6 ${
          collections.length === 1 ? 'max-w-md mx-auto' :
          collections.length === 2 ? 'md:grid-cols-2 max-w-2xl mx-auto' :
          collections.length === 3 ? 'md:grid-cols-3' :
          'md:grid-cols-2 lg:grid-cols-4'
        }`}>
          {collections.map((collection, index) => (
            <motion.div
              key={collection.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <Link
                to={`/menu/${collection.slug}`}
                className="group block bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                {/* Image */}
                <div className="aspect-[4/3] overflow-hidden bg-gradient-to-br from-pink-100 to-purple-100">
                  {collection.image_url ? (
                    <img
                      src={collection.image_url}
                      alt={collection.name}
                      className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        // Show fallback emoji if image fails
                        const img = e.target as HTMLImageElement;
                        img.style.display = 'none';
                        const parent = img.parentElement;
                        if (parent && !parent.querySelector('.fallback-emoji')) {
                          const fallback = document.createElement('div');
                          fallback.className = 'w-full h-full flex items-center justify-center fallback-emoji';
                          fallback.innerHTML = '<span class="text-6xl">🎂</span>';
                          parent.appendChild(fallback);
                        }
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-6xl">🎂</span>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-5">
                  <h3 className="font-display font-bold text-lg text-gray-900 group-hover:text-primary-600 transition-colors">
                    {collection.name}
                  </h3>
                  {collection.description && (
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                      {collection.description}
                    </p>
                  )}
                  <div className="mt-3 flex items-center gap-1 text-primary-600 text-sm font-medium">
                    <span>View Menu</span>
                    <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
